import { createFileRoute, Link } from '@tanstack/react-router'
import { AccessDenied } from '@/components/permission-guard'
import { useEvent } from '@/lib/api/queries'
import { usePermission } from '@/lib/permissions'
import { cn } from '@/lib/utils'
import { OverviewTab } from '@/features/fraud/overview-tab'
import { UsersTab } from '@/features/fraud/users-tab'
import { BlocksTab } from '@/features/fraud/blocks-tab'
import { TrustedTab } from '@/features/fraud/trusted-tab'
import { ChargebacksTab } from '@/features/fraud/chargebacks-tab'
import { PolicyTab } from '@/features/fraud/policy-tab'

const TABS = ['overview', 'users', 'blocks', 'trusted', 'chargebacks', 'policy'] as const
type Tab = (typeof TABS)[number]
const TAB_LABEL: Record<Tab, string> = {
  overview: 'Overview', users: 'Attendees', blocks: 'Blocks', trusted: 'Trusted', chargebacks: 'Chargebacks', policy: 'Policy',
}

export const Route = createFileRoute('/_authenticated/events/$eventId/fraud')({
  component: FraudPage,
  validateSearch: (search: Record<string, unknown>): { tab?: Tab } => {
    const tab = typeof search.tab === 'string' && (TABS as readonly string[]).includes(search.tab) ? (search.tab as Tab) : undefined
    return tab ? { tab } : {}
  },
})

function FraudPage() {
  const { eventId } = Route.useParams()
  const { tab = 'overview' } = Route.useSearch()
  const eventIdNum = Number(eventId)
  const canViewFraud = usePermission('can_view_fraud')
  const { data: event } = useEvent(eventIdNum)

  // Permission guard - now safe after all hooks
  if (!canViewFraud) {
    return <AccessDenied message="You don't have permission to view fraud monitoring for this event." />
  }

  const currency = event?.currency

  return (
    <div className="max-w-[100vw] md:max-w-[1300px] w-full mx-auto px-4 md:px-8 py-6 space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-white">Risk</h1>
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
          {event?.name ? `${event.name} · ` : ''}Card-reload risk at this event: who is being challenged or refused, why, and the controls behind it.
        </p>
      </div>

      <nav className="flex gap-1 border-b border-neutral-200 dark:border-neutral-800 overflow-x-auto">
        {TABS.map((t) => (
          <Link
            key={t}
            to="/events/$eventId/fraud"
            params={{ eventId }}
            search={t === 'overview' ? {} : { tab: t }}
            className={cn('px-3 py-2 text-sm whitespace-nowrap border-b-2 -mb-px',
              tab === t
                ? 'border-blue-600 text-blue-600 dark:text-blue-400 font-medium'
                : 'border-transparent text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white')}
          >
            {TAB_LABEL[t]}
          </Link>
        ))}
      </nav>

      {tab === 'overview' && <OverviewTab eventId={eventIdNum} currency={currency} />}
      {tab === 'users' && <UsersTab eventId={eventIdNum} currency={currency} />}
      {tab === 'blocks' && <BlocksTab eventId={eventIdNum} />}
      {tab === 'trusted' && <TrustedTab eventId={eventIdNum} />}
      {tab === 'chargebacks' && <ChargebacksTab eventId={eventIdNum} currency={currency} />}
      {tab === 'policy' && <PolicyTab eventId={eventIdNum} currency={currency} />}
    </div>
  )
}
